import { useFeatureFlags } from '@node-space/hooks'
import {
  AccountContactStepKeys,
  AccountContactTypesList,
} from 'components/accountContact/helpers/constants'
import { AccountContactData } from 'components/accountContact/helpers/types'
import { useAccountContactList } from 'components/accountContact/hooks/useAccountContactList'
import { AddressFormKeysV2 } from 'components/AddressFormFields/constants'
import { getIsStateRequired } from 'components/AddressFormFields/utils'
import { DynamicFieldsDefaultValue } from 'constants/dynamicFields'
import {
  VerificationRouteList,
  VerificationRoutes,
  VerificationRouteStatus,
} from 'constants/verification'
import { FinpromVerificationStepList } from 'pages/Finprom/constants'
import { finpromPreferenceKey } from 'pages/Finprom/finpromPreferenceData'
import { FinpromVerificationStep, FinpromVerificationStepKey } from 'pages/Finprom/types'
import { BusinessAddressData } from 'pages/Verification/BusinessAddress/BusinessAddressV2/helpers/types'
import { MappedQuestionCodes } from 'pages/Verification/BusinessCompliance/helpers/questionMapping'
import { AllDocumentCodeList } from 'pages/Verification/UploadDocuments/UploadDocumentsV2/helpers/constants'
import { DocumentCode } from 'pages/Verification/UploadDocuments/UploadDocumentsV2/helpers/types'
import { DynamicFieldsData } from 'types/dynamicFields'
import {
  BusinessVerificationStatus,
  VerificationRouteKey,
  VerificationRouteStatus as VerificationRouteStatusType,
  VerificationStep,
} from 'types/verification'
import { useFinpromContext } from './context/useFinpromContext'
import { useProfileContext } from './context/useProfileContext'
import { useVerticalContext } from './context/useVerticalContext'

export type VerificationStepsRequiredData = {
  businessVerificationStatus: BusinessVerificationStatus
  businessAddress: BusinessAddressData
  accountContacts: AccountContactData[]
  dynamicFields: DynamicFieldsData
}

export const useVerificationStepsRequired = ({
  businessVerificationStatus,
  businessAddress,
  accountContacts,
  dynamicFields,
}: VerificationStepsRequiredData) => {
  const { enableCorporateStructure, enableCountryState, enableAppointSignatory } = useFeatureFlags()
  const { businessDetails, industryName } = useVerticalContext()
  const { accountPreferences, showFinpromPhaseTwo, isWatchingBusinessAddress } = useFinpromContext()
  const { getContactStepStatus } = useAccountContactList(accountContacts)
  const { documents: dynamicDocuments, questions: dynamicQuestions } =
    dynamicFields || (DynamicFieldsDefaultValue as DynamicFieldsData)
  const { profile: { emailAddress } = {} } = useProfileContext() || {}

  const getFinpromStepStatus = (
    stepKey: FinpromVerificationStepKey
  ): VerificationRouteStatusType => {
    const preferenceKey = finpromPreferenceKey?.[stepKey]
    const isStepComplete = !!accountPreferences?.[preferenceKey]?.isComplete
    return isStepComplete ? VerificationRouteStatus.COMPLETED : VerificationRouteStatus.PENDING
  }

  const finpromVerificationSteps: FinpromVerificationStep[] =
    showFinpromPhaseTwo && !isWatchingBusinessAddress
      ? FinpromVerificationStepList?.map(key => ({
          key,
          status: getFinpromStepStatus(key),
        }))
      : []

  const getBusinessAddressStepStatus = () => {
    if (businessAddress?.registered) {
      const isStateRequired =
        !!enableCountryState && getIsStateRequired(businessAddress?.registered?.countryCode)
      return Object.values(AddressFormKeysV2)
        ?.filter(
          key =>
            key !== AddressFormKeysV2.ADDRESS_LINE_2 &&
            (isStateRequired || key !== AddressFormKeysV2.COUNTRY_STATE_CODE)
        )
        ?.every(key => !!businessAddress?.registered?.[key])
    }
  }

  const getBusinessDetailsStepStatus = () => {
    if (businessDetails?.industryReference && !industryName) return false
  }

  const getAccountContactStepStatus = (stepKey: VerificationRouteKey) => {
    const accountContactType = AccountContactTypesList?.find(
      key => AccountContactStepKeys[key] === stepKey
    )
    return getContactStepStatus(accountContactType)
  }

  const getBusinessComplianceStepStatus = () => {
    if (!dynamicQuestions?.length) return
    return dynamicQuestions
      ?.filter(({ code }) => MappedQuestionCodes?.includes(code))
      ?.every(({ provided }) => provided)
  }

  const getUploadDocumentsStepStatus = () => {
    if (!dynamicDocuments?.length) return
    return dynamicDocuments
      ?.filter(({ code }) => AllDocumentCodeList?.includes(code as DocumentCode))
      ?.every(({ provided }) => provided)
  }

  const getAppointSignatoryStepStatus = () => {
    if (accountPreferences?.selectedSignatory?.isMyself) return true

    return (
      !!accountPreferences?.selectedSignatory?.reference &&
      emailAddress === accountPreferences?.selectedSignatory?.emailAddress
    )
  }

  const getDynamicStepStatus = (stepKey: VerificationRouteKey): boolean => {
    switch (stepKey) {
      case VerificationRoutes.BUSINESS_ADDRESS:
        return getBusinessAddressStepStatus()
      case VerificationRoutes.BUSINESS_DETAILS:
        return getBusinessDetailsStepStatus()
      case VerificationRoutes.ACCOUNT_REPRESENTATIVE:
      case VerificationRoutes.BUSINESS_OWNERS:
      case VerificationRoutes.BUSINESS_DIRECTORS:
        return getAccountContactStepStatus(stepKey)
      case VerificationRoutes.BUSINESS_COMPLIANCE:
        return getBusinessComplianceStepStatus()
      case VerificationRoutes.UPLOAD_DOCUMENTS:
        return getUploadDocumentsStepStatus()
      case VerificationRoutes.APPOINT_SIGNATORY:
        return getAppointSignatoryStepStatus()
      default:
        return
    }
  }

  const getStepStatus = (stepKey: VerificationRouteKey): VerificationRouteStatusType => {
    const dataStepStatus = businessVerificationStatus?.[stepKey]
    const dynamicStepStatus = getDynamicStepStatus(stepKey)
    if (typeof dynamicStepStatus === 'boolean') {
      return dynamicStepStatus ? VerificationRouteStatus.COMPLETED : VerificationRouteStatus.PENDING
    }
    return dataStepStatus || VerificationRouteStatus.PENDING
  }

  const getIsStepDisabled = (stepKey: VerificationRouteKey, isLockedStep: boolean) => {
    const hasIncompleteFinpromSteps = finpromVerificationSteps?.some(
      ({ status }) => status !== VerificationRouteStatus.COMPLETED
    )
    return hasIncompleteFinpromSteps || isLockedStep
  }

  const getRequiredStepList = () => {
    const hasRequiredDynamicQuestions = dynamicQuestions?.some(({ code }) =>
      MappedQuestionCodes?.includes(code)
    )
    return VerificationRouteList?.filter(step => {
      const showBusinessComplianceStep =
        hasRequiredDynamicQuestions || step !== VerificationRoutes.BUSINESS_COMPLIANCE
      const showCorporateStructure =
        !!enableCorporateStructure || step !== VerificationRoutes.CORPORATE_STRUCTURE
      const showAppointSignatory =
        !!enableAppointSignatory || step !== VerificationRoutes.APPOINT_SIGNATORY
      return showBusinessComplianceStep && showCorporateStructure && showAppointSignatory
    })
  }

  const requiredVerificationSteps = getRequiredStepList()

  const getVerificationSteps = (): VerificationStep[] => {
    let isFirstPendingStep = true
    return requiredVerificationSteps?.map((key: VerificationRouteKey) => {
      const status = getStepStatus(key)
      const isLockedStep = !isFirstPendingStep && status !== VerificationRouteStatus.COMPLETED
      const disabled = getIsStepDisabled(key, isLockedStep)
      const step: VerificationStep = { key, status, disabled }
      if (status === VerificationRouteStatus.PENDING && isFirstPendingStep) {
        isFirstPendingStep = false
      }
      return step
    })
  }

  const verificationSteps = getVerificationSteps()

  const hasCompletedSteps = verificationSteps?.some(
    ({ status }) => status === VerificationRouteStatus.COMPLETED
  )

  return { finpromVerificationSteps, verificationSteps, hasCompletedSteps }
}
